<template>
  <nav class="tw:flex tw:justify-center tw:gap-1.5" role="navigation" aria-label="Pagination">
    <button
      type="button"
      :class="[
        'tw:flex tw:items-center tw:justify-center tw:w-8 tw:h-8 tw:rounded tw:transition-all tw:duration-100 tw:border-none tw:bg-transparent',
        isFirstPage ? 'tw:cursor-not-allowed tw:opacity-50' : 'tw:cursor-pointer hover:tw:bg-(--surface-color)',
      ]"
      :aria-disabled="isFirstPage"
      aria-label="Previous page"
      @click="handlePrev"
    >
      <IconGoogle size="1rem" name="chevron_left" color="var(--fontcolor)" />
    </button>

    <button
      v-for="page in visiblePages"
      :key="page"
      type="button"
      :class="[
        'tw:flex tw:items-center tw:justify-center tw:w-8 tw:h-8 tw:rounded tw:cursor-pointer tw:transition-all tw:duration-100 tw:border-none',
        currentPageNum === page
          ? 'tw:bg-(--fontcolor)/10 tw:text-(--fontcolor)'
          : 'tw:bg-transparent tw:text-(--fontcolor) hover:tw:bg-(--surface-color)',
      ]"
      :aria-current="currentPageNum === page ? 'page' : undefined"
      :aria-label="`Page ${page}`"
      @click="goToPage(page)"
    >
      <TextPlay
        weight="semibold"
        :text="String(page)"
        size="sm"
        :color="currentPageNum === page ? 'var(--fontcolor)' : 'var(--text-color)'"
      />
    </button>

    <button
      type="button"
      :class="[
        'tw:flex tw:items-center tw:justify-center tw:w-8 tw:h-8 tw:rounded tw:transition-all tw:duration-100 tw:border-none tw:bg-transparent',
        isLastPage ? 'tw:cursor-not-allowed tw:opacity-50' : 'tw:cursor-pointer hover:tw:bg-(--surface-color)',
      ]"
      :aria-disabled="isLastPage"
      aria-label="Next page"
      @click="handleNext"
    >
      <IconGoogle size="1rem" name="chevron_right" color="var(--fontcolor)" />
    </button>
  </nav>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import IconGoogle from '../../atoms/IconGoogle/IconGoogle.vue'
import TextPlay from '../../atoms/TextPlay/TextPlay.vue'

const props = withDefaults(defineProps<{
  /** Current active page (supports v-model) */
  modelValue?: number
  /** @deprecated Use v-model instead */
  currentPage?: number | string
  /** Total number of items */
  total: number
  /** Items per page */
  perPage: number
  /** Maximum number of page buttons visible */
  maxVisible?: number
}>(), {
  modelValue: undefined,
  currentPage: undefined,
  maxVisible: 4,
})

const emit = defineEmits<{
  'update:modelValue': [page: number]
  /** @deprecated Use v-model instead */
  'onPrev': []
  /** @deprecated Use v-model instead */
  'onNext': []
  /** @deprecated Use v-model instead */
  'onSelectPage': [page: number]
}>()

// Support both v-model and legacy currentPage prop
const currentPageNum = computed(() => {
  if (props.modelValue !== undefined) return props.modelValue
  return Number(props.currentPage ?? 1)
})

const totalPages = computed(() => Math.max(1, Math.ceil(props.total / props.perPage)))
const isFirstPage = computed(() => currentPageNum.value <= 1)
const isLastPage = computed(() => currentPageNum.value >= totalPages.value)

const visiblePages = computed(() => {
  const pages = totalPages.value
  const current = currentPageNum.value
  const max = props.maxVisible

  if (pages <= 0) return []
  if (pages <= max) {
    return Array.from({ length: pages }, (_, i) => i + 1)
  }

  const half = Math.floor(max / 2)
  let start = current - half
  let end = start + max - 1

  if (start < 1) {
    start = 1
    end = max
  }

  if (end > pages) {
    end = pages
    start = pages - max + 1
  }

  return Array.from({ length: end - start + 1 }, (_, i) => start + i)
})

function goToPage(page: number) {
  emit('update:modelValue', page)
  emit('onSelectPage', page)
}

function handlePrev() {
  if (isFirstPage.value) return
  const page = currentPageNum.value - 1
  emit('update:modelValue', page)
  emit('onSelectPage', page)
  emit('onPrev')
}

function handleNext() {
  if (isLastPage.value) return
  const page = currentPageNum.value + 1
  emit('update:modelValue', page)
  emit('onSelectPage', page)
  emit('onNext')
}
</script>
